import React, { useContext, useEffect, useState } from "react";
import { AuthContext, AuthProvider } from './AuthContext';
import { TENANT_AUTH } from './authMode';
import { BrowserRouter as Router, Route, Routes, Link, useLocation, useNavigate, Navigate } from "react-router-dom";
import { Button, Box, Collapse, Paper, Stack, Alert, Typography } from "@mui/material";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Personen from "./pages/Personen";
import Strecke from "./pages/Strecke";
import Verkauf from "./pages/verkauf";
import Umsaetze from "./pages/Umsaetze";
import Finanzen from "./pages/Finanzen";
import Lieferscheine from "./pages/Lieferscheine";
import Rechnungen from "./pages/Rechnungen";
import { canReadInvoices } from "./components/invoiceAccess";
import LocalShipping from "@mui/icons-material/LocalShipping";
import { canReadDeliveryNotes } from "./components/deliveryAccess";
import { canAccessFinance } from "./components/financeAccess";
import Revierkarte from "./pages/Revierkarte";
import Bestellungen from "./pages/Bestellungen";
import Kategorien from "./pages/Kategorien";
import Artikel from "./pages/Artikel";
import Einrichtungen from "./pages/Einrichtungen";
import Forest from "@mui/icons-material/Forest";
import { canReadEinrichtungen } from "./components/einrichtungAccess";
import Inventory2 from "@mui/icons-material/Inventory2";
import { canReadArticles } from "./components/articleAccess";
import ProtectedRoute from "./components/ProtectedRoute";
import DatabaseStatus from "./components/DatabaseStatus";
import VersionInfo from "./components/VersionInfo";
import LoadingIndicator from './components/LoadingIndicator';
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDialogDayjs as AdapterDayjs } from "./AdapterDialogDayjs";
import { ThemeProvider } from "@mui/material";
import theme from "./theme";
import deLocale from "dayjs/locale/de";
import { BusinessProvider, useBusinessContext } from "./BusinessContext";
import BusinessSelection from "./components/BusinessSelection";
import AccountBalanceWallet from "@mui/icons-material/AccountBalanceWallet";
import AccountBalance from "@mui/icons-material/AccountBalance";
import Groups from "@mui/icons-material/Groups";
import LogoutIcon from "@mui/icons-material/Logout";
import Map from "@mui/icons-material/Map";
import Payments from "@mui/icons-material/Payments";
import { JaegerIcon, KundeIcon } from "./components/icons/JagdverwaltungIcons";
import ShoppingCart from "@mui/icons-material/ShoppingCart";
import GpsFixed from "@mui/icons-material/GpsFixed";
import ReceiptLong from "@mui/icons-material/ReceiptLong";
import Category from "@mui/icons-material/Category";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import NaturePeople from "@mui/icons-material/NaturePeople";
import Storefront from "@mui/icons-material/Storefront";
import Euro from "@mui/icons-material/Euro";
import Storage from "@mui/icons-material/Storage";
import SysAdmin, { SysAdminGuard } from './pages/SysAdmin';


function NavigationGroup({ id, label, icon, open, onToggle, children }) {
  return <Box>
    <Button
      variant="outlined"
      startIcon={icon}
      endIcon={open ? <ExpandLess /> : <ExpandMore />}
      onClick={onToggle}
      aria-expanded={open}
      aria-controls={`${id}-navigation`}
      fullWidth
      sx={{
        justifyContent: "flex-start",
        minHeight: 32,
        px: 1,
        py: 0.5,
        color: "primary.dark",
        borderColor: "primary.main",
        bgcolor: "rgba(58, 101, 66, 0.10)",
        fontSize: "0.78rem",
        fontWeight: 700,
        lineHeight: 1.2,
        "& .MuiButton-startIcon": { mr: 0.75 },
        "& .MuiButton-endIcon": { ml: "auto", mr: -0.5 },
        "&:hover": { bgcolor: "rgba(58, 101, 66, 0.18)", borderColor: "primary.dark" },
      }}
    >
      {label}
    </Button>
    <Collapse in={open} timeout="auto" unmountOnExit>
      <Stack id={`${id}-navigation`} spacing={0.5} sx={{ pt: 0.5, pl: 1 }}>
        {children}
      </Stack>
    </Collapse>
  </Box>;
}

function Navigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useContext(AuthContext);
  const [logoutError, setLogoutError] = useState('');
  const logout = () => {
    if (TENANT_AUTH) {
      auth.logout().then(() => navigate('/login', { replace: true }))
        .catch(() => setLogoutError('Abmeldung fehlgeschlagen. Bitte erneut versuchen.'));
      return;
    }
    localStorage.removeItem("token");
    navigate("/login", { replace: true });
  };
  const search = new URLSearchParams(location.search);
  const modus = search.get("modus") || "all";
  const activeGroup = location.pathname === "/strecke" || location.pathname === "/revierkarte"
    || location.pathname === "/einrichtungen" || (location.pathname === "/personen" && modus === "jaeger")
    ? "jagdbetrieb"
    : location.pathname === "/verkauf" || location.pathname === "/bestellungen"
      || location.pathname === "/lieferscheine" || location.pathname === "/rechnungen"
      || (location.pathname === "/personen" && modus === "kunden")
      ? "vermarktung"
      : location.pathname === "/finanzen" || location.pathname === "/umsaetze"
        ? "finanzen"
        : location.pathname === "/kategorien" || location.pathname === "/artikel"
          || (location.pathname === "/personen" && modus === "all")
          ? "stammdaten"
          : null;
  const [openGroups, setOpenGroups] = useState(() => activeGroup ? { [activeGroup]: true } : {});
  useEffect(() => {
    if (!activeGroup) return;
    setOpenGroups(previous => previous[activeGroup] ? previous : { ...previous, [activeGroup]: true });
  }, [activeGroup]);
  const toggleGroup = group => setOpenGroups(previous => ({ ...previous, [group]: !previous[group] }));
  const buttonSx = (active) => ({
    justifyContent: "flex-start",
    minHeight: 32,
    px: 1,
    py: 0.5,
    fontSize: "0.78rem",
    lineHeight: 1.2,
    bgcolor: active ? "primary.dark" : "primary.main",
    "& .MuiButton-startIcon": { mr: 0.75 },
  });
  return (
    <Stack spacing={0.75} sx={{ width: "100%", boxSizing: "border-box", mt: 1, px: 0.75 }}>
      <Button variant="contained" startIcon={<DashboardIcon />} sx={buttonSx(location.pathname === "/dashboard")}
        aria-current={location.pathname === "/dashboard" ? "page" : undefined}
        component={Link} to="/dashboard" fullWidth>Dashboard</Button>
      <NavigationGroup id="jagdbetrieb" label="Jagdbetrieb" icon={<NaturePeople />}
        open={Boolean(openGroups.jagdbetrieb)} onToggle={() => toggleGroup("jagdbetrieb")}>
        <Button variant="contained" startIcon={<GpsFixed />} sx={buttonSx(location.pathname === "/strecke")}
          aria-current={location.pathname === "/strecke" ? "page" : undefined}
          component={Link} to="/strecke" fullWidth>Strecke</Button>
        <Button variant="contained" startIcon={<Map />} sx={buttonSx(location.pathname === "/revierkarte")}
          aria-current={location.pathname === "/revierkarte" ? "page" : undefined}
          component={Link} to="/revierkarte" fullWidth>Revierkarte</Button>
        <Button variant="contained" startIcon={<JaegerIcon />}
          sx={buttonSx(location.pathname === "/personen" && modus === "jaeger")}
          aria-current={location.pathname === "/personen" && modus === "jaeger" ? "page" : undefined}
          component={Link} to="/personen?modus=jaeger" fullWidth>Jäger</Button>
        {canReadEinrichtungen(auth?.user) && <Button variant="contained" startIcon={<Forest />}
          sx={buttonSx(location.pathname === "/einrichtungen")}
          aria-current={location.pathname === "/einrichtungen" ? "page" : undefined}
          component={Link} to="/einrichtungen" fullWidth>Einrichtungen</Button>}
      </NavigationGroup>
      <NavigationGroup id="vermarktung" label="Vermarktung" icon={<Storefront />}
        open={Boolean(openGroups.vermarktung)} onToggle={() => toggleGroup("vermarktung")}>
        <Button variant="contained" startIcon={<ShoppingCart />} sx={buttonSx(location.pathname === "/verkauf")}
          aria-current={location.pathname === "/verkauf" ? "page" : undefined}
          component={Link} to="/verkauf" fullWidth>Verkäufe</Button>
        <Button variant="contained" startIcon={<ReceiptLong />} sx={buttonSx(location.pathname === "/bestellungen")}
          aria-current={location.pathname === "/bestellungen" ? "page" : undefined}
          component={Link} to="/bestellungen" fullWidth>Bestellungen</Button>
        <Button variant="contained" startIcon={<KundeIcon />}
          sx={buttonSx(location.pathname === "/personen" && modus === "kunden")}
          aria-current={location.pathname === "/personen" && modus === "kunden" ? "page" : undefined}
          component={Link} to="/personen?modus=kunden" fullWidth>Kunden</Button>
        {canReadDeliveryNotes(auth?.user) && <Button variant="contained" startIcon={<LocalShipping />}
          sx={buttonSx(location.pathname === "/lieferscheine")}
          aria-current={location.pathname === "/lieferscheine" ? "page" : undefined}
          component={Link} to="/lieferscheine" fullWidth>Lieferscheine</Button>}
        {canReadInvoices(auth?.user) && <Button variant="contained" startIcon={<ReceiptLong />}
          sx={buttonSx(location.pathname === "/rechnungen")}
          aria-current={location.pathname === "/rechnungen" ? "page" : undefined}
          component={Link} to="/rechnungen" fullWidth>Rechnungen</Button>}
      </NavigationGroup>
      <NavigationGroup id="finanzen" label="Finanzen" icon={<Euro />}
        open={Boolean(openGroups.finanzen)} onToggle={() => toggleGroup("finanzen")}>
        {canAccessFinance(auth?.user) && <Button variant="contained" startIcon={<AccountBalance />}
          sx={buttonSx(location.pathname === "/finanzen")}
          aria-current={location.pathname === "/finanzen" ? "page" : undefined}
          component={Link} to="/finanzen" fullWidth>Finanzübersicht</Button>}
        <Button variant="contained" startIcon={<Payments />}
          sx={buttonSx(location.pathname === "/umsaetze" && modus === "einnahmen")}
          aria-current={location.pathname === "/umsaetze" && modus === "einnahmen" ? "page" : undefined}
          component={Link} to="/umsaetze?modus=einnahmen" fullWidth>Einnahmen</Button>
        <Button variant="contained" startIcon={<AccountBalanceWallet />}
          sx={buttonSx(location.pathname === "/umsaetze" && modus === "ausgaben")}
          aria-current={location.pathname === "/umsaetze" && modus === "ausgaben" ? "page" : undefined}
          component={Link} to="/umsaetze?modus=ausgaben" fullWidth>Ausgaben</Button>
      </NavigationGroup>
      <NavigationGroup id="stammdaten" label="Stammdaten" icon={<Storage />}
        open={Boolean(openGroups.stammdaten)} onToggle={() => toggleGroup("stammdaten")}>
        <Button variant="contained" startIcon={<Groups />}
          sx={buttonSx(location.pathname === "/personen" && modus === "all")}
          aria-current={location.pathname === "/personen" && modus === "all" ? "page" : undefined}
          component={Link} to="/personen" fullWidth>Personen</Button>
        <Button variant="contained" startIcon={<Category />} sx={buttonSx(location.pathname === "/kategorien")}
          aria-current={location.pathname === "/kategorien" ? "page" : undefined}
          component={Link} to="/kategorien" fullWidth>Kategorien</Button>
        {canReadArticles(auth?.user) && <Button variant="contained" startIcon={<Inventory2 />}
          sx={buttonSx(location.pathname === "/artikel")}
          aria-current={location.pathname === "/artikel" ? "page" : undefined}
          component={Link} to="/artikel" fullWidth>Artikel</Button>}
      </NavigationGroup>
      <Button variant="contained" startIcon={<LogoutIcon />} onClick={logout} fullWidth sx={buttonSx(false)}>
        Abmelden
      </Button>
      {logoutError && <Alert severity="error">{logoutError}</Alert>}
    </Stack>
  );
}

function Kopfzeile() {
  const { selectedMandant } = useBusinessContext();
  return <Box component="header" data-testid="global-header" sx={{ mb: 1.5 }}>
    <Typography variant="h6" component="h1" data-testid="application-title">
      Verwaltung {selectedMandant?.Name || "Jagdverwaltung"}
    </Typography>
  </Box>;
}

function AppLayout() {
  return <BusinessProvider><ScopedLayout /></BusinessProvider>;
}

function ScopedLayout() {
  const { ready, loading, error, scopeKey, mandantId, betriebId } = useBusinessContext();
  const location = useLocation();
  const modus = new URLSearchParams(location.search).get("modus") || "all";
  const globalPage = ["/bestellungen", "/kategorien", "/artikel", "/revierkarte", "/einrichtungen"].includes(location.pathname)
    || (location.pathname === "/umsaetze" && Boolean(mandantId))
    || (location.pathname === "/strecke" && Boolean(mandantId))
    || (location.pathname === "/personen" && modus === "all");
  const routeKey = globalPage ? ({
    "/personen": `personen:${mandantId}`,
    "/bestellungen": `bestellungen:${mandantId}:${betriebId}`,
    "/kategorien": `kategorien:${mandantId}`,
    "/artikel": `artikel:${mandantId}`,
    "/einrichtungen": `einrichtungen:${mandantId}:${betriebId}`,
    "/umsaetze": `umsaetze:${mandantId}`,
    "/strecke": `strecke:${mandantId}`,
  }[location.pathname] || "global") : scopeKey;
  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <Paper elevation={2} sx={{ width: { xs: 144, sm: 190 }, flexShrink: 0, borderRadius: 0 }}>
        <Box sx={{
          width: "100%",
          boxSizing: "border-box",
          px: 1,
          py: 1.25,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: { xs: 0.75, sm: 1 },
        }}>
          <Box
            component="img"
            src="/javeplus-64x64.png"
            alt="JavePlus App-Icon"
            sx={{ width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 }, flexShrink: 0 }}
          />
          <Typography
            component="h1"
            sx={{ fontSize: { xs: "1rem", sm: "1.25rem" }, fontWeight: 700, lineHeight: 1 }}
          >
            JAVEPLUS
          </Typography>
        </Box>
        <Navigation />
        <BusinessSelection />
        <DatabaseStatus />
        <VersionInfo />
      </Paper>
      <Box sx={{ flex: 1, minWidth: 0, px: { xs: 1.5, sm: 2 }, pt: { xs: 1, sm: 1.5 }, pb: 2 }}>
        <Kopfzeile />
        {ready || globalPage || (location.pathname === "/personen" && mandantId && betriebId) ? <Routes key={routeKey}>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/home" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/personen" element={<Personen modus="all" />} />
          <Route path="/jaeger" element={<Navigate to="/personen?modus=jaeger" replace />} />
          <Route path="/kunden" element={<Navigate to="/personen?modus=kunde" replace />} />
          <Route path="/strecke" element={<Strecke />} />
          <Route path="/verkauf" element={<Verkauf />} />
          <Route path="/bestellungen" element={<Bestellungen />} />
          <Route path="/umsaetze" element={<Umsaetze />} />
          <Route path="/finanzen" element={<Finanzen />} />
          <Route path="/lieferscheine" element={<Lieferscheine />} />
          <Route path="/rechnungen" element={<Rechnungen />} />
          <Route path="/kategorien" element={<Kategorien />} />
          <Route path="/artikel" element={<Artikel />} />
          <Route path="/einrichtungen" element={<Einrichtungen />} />
          <Route path="/ausgaben" element={<Navigate to="/umsaetze?modus=ausgaben" replace />} />
          <Route path="/revierkarte" element={<Revierkarte />} />
        </Routes> : loading ? <LoadingIndicator label="Geschäftskontext wird geladen …" /> : <Alert severity={error ? "error" : "info"}>{error || "Bitte einen vollständigen Geschäftskontext auswählen."}</Alert>}
      </Box>
    </Box>
  );
}

function App() {
  return TENANT_AUTH ? <AuthProvider><Application /></AuthProvider> : <Application />;
}

function Application() {
  return (
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={deLocale}>
          <Router>
            <Routes>
              {/* Login OHNE Layout */}
              <Route path="/login" element={<Login />} />
              <Route path="/sysadmin/*" element={<ProtectedRoute><SysAdminGuard><SysAdmin /></SysAdminGuard></ProtectedRoute>} />
              {/* Alles andere MIT Layout */}
              <Route path="/*" element={<ProtectedRoute><AppLayout /></ProtectedRoute>} />
            </Routes>
          </Router>
      </LocalizationProvider>
    </ThemeProvider>
  );
}

export default App;
